import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import { FieldLabel } from "../../../atoms/FieldLabel";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { Copy } from "../../../atoms/Copy";
import PaymentModuleService, {
  GuaranteeRefundDTO,
} from "../../../../services/paymentModule";
import {
  formatPayModDate,
  newRequestId,
  parsePayModDate,
} from "../../../../services/planChange";
import { GuaranteeTargetPlan } from "./sumPlans";
import { formatAmount } from "./types";

const TEXT: Record<string, string> = {
  amount: "Amount to refund",
  amountHint:
    "Prefilled with the last payment recorded, {{amount}} on {{date}}. Leave empty to refund what is left of the last payment.",
  amountHintNoDate:
    "Prefilled with the last payment recorded, {{amount}}. Leave empty to refund what is left of the last payment.",
  amountHintUnknown:
    "No payment amount found. Leave empty to refund what is left of the last payment.",
  amountInvalid: "Enter a positive amount, with at most two decimals.",
  back: "Back",
  cancel: "Cancel",
  close: "Close",
  confirm: "Refund",
  confirmLead: "This refund is final: it goes to Stripe at once.",
  done: "Refund sent to Stripe.",
  nothingRefunded: "Nothing was refunded.",
  plan: "Paid plan",
  reason: "Reason",
  reasonPlaceholder: "Reason for this refund",
  reasonRequired: "The reason is required.",
  "refusals.ALREADY_REFUNDED": "The last payment is already refunded in full.",
  "refusals.AMOUNT_TOO_HIGH":
    "The amount is above what can still be refunded on the last payment.",
  "refusals.NO_PAID_PAYMENT":
    "This subscription has no card payment to refund.",
  "refusals.NO_SUBSCRIPTION":
    "The account's owner has no Stripe subscription to this plan.",
  "refusals.NOT_SUM_SUBSCRIPTION":
    "This plan is not a SUM subscription: it cannot be refunded here.",
  "refusals.PAID_BY_PAYER":
    "This plan is paid by another account, a distributor for instance: it is not refunded here.",
  "refusals.PAYMENT_LINK":
    "A sale through a payment link is refunded by its merchant, not here.",
  "refusals.REFUSED": "The payment module refused the refund: {{message}}",
  "refusals.REFUSED_ANY": "The payment module refused the refund.",
  "refusals.refundableLeft": "Still refundable: {{amount}}.",
  "result.amount": "Amount refunded",
  "result.currency": "Currency",
  "result.refundId": "Refund ID",
  "result.status": "Status",
  retry: "Try again",
  review: "Review",
  "status.canceled": "Canceled",
  "status.failed": "Failed",
  "status.other": "Other",
  "status.pending": "Pending",
  "status.requires_action": "Action required",
  "status.succeeded": "Succeeded",
  subscriptionKept:
    "Refund only: the subscription keeps running. Cancel it separately if needed.",
  title: "Refund",
  uncertain:
    "The payment module gave no definite answer. Try again: the same request is sent, and it never refunds twice.",
  wholeLastPayment: "What is left of the last payment",
};

const k = (key: string, values: Record<string, unknown> = {}) =>
  (TEXT[key] ?? key).replace(/\{\{(\w+)\}\}/g, (_, name) =>
    String(values[name] ?? "")
  );

const REFUSALS = [
  "NOT_SUM_SUBSCRIPTION",
  "NO_SUBSCRIPTION",
  "PAYMENT_LINK",
  "PAID_BY_PAYER",
  "AMOUNT_TOO_HIGH",
  "ALREADY_REFUNDED",
  "NO_PAID_PAYMENT",
  "REFUSED",
];

const STRIPE_STATUSES = [
  "succeeded",
  "pending",
  "requires_action",
  "failed",
  "canceled",
];

const REASON_MAX = 500;

const INPUT =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 md:py-2 md:text-sm";

const toCents = (text: string): number | null | undefined => {
  const value = text.trim().replace(",", ".");
  if (value === "") return undefined;
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const cents = Math.round(parseFloat(value) * 100);
  return cents > 0 ? cents : null;
};

const amountOf = (plan: GuaranteeTargetPlan | null) =>
  plan?.lastPayment ? (plan.lastPayment.cents / 100).toFixed(2) : "";

const money = (cents: number, currency: string) =>
  currency ? formatAmount(cents / 100, currency) : (cents / 100).toFixed(2);

type Step = "form" | "confirm" | "done";

interface GuaranteeRefundModalProps {
  show: boolean;
  onClose: () => void;
  customerId: string;
  rootEntityId: string;
  entityId?: string;
  entityName?: string;
  plan: GuaranteeTargetPlan | null;
  onRefunded: () => void;
}

export const GuaranteeRefundModal: React.FC<GuaranteeRefundModalProps> = ({
  show,
  onClose,
  customerId,
  rootEntityId,
  entityId,
  entityName,
  plan,
  onRefunded,
}) => {
  const [step, setStep] = useState<Step>("form");
  const [amountText, setAmountText] = useState("");
  const [reason, setReason] = useState("");
  const [requestId, setRequestId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [result, setResult] = useState<GuaranteeRefundDTO | null>(null);

  const currency = plan?.lastPayment?.currency ?? "";
  const cents = toCents(amountText);
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (!show) return;
    setStep("form");
    setReason("");
    setRequestId(newRequestId());
    setShowErrors(false);
    setRefusal(null);
    setUncertain(false);
    setResult(null);
    setAmountText(amountOf(plan));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, plan?.planCode]);

  const review = () => {
    setShowErrors(true);
    if (!plan || cents === null || !trimmedReason) return;
    setRefusal(null);
    setStep("confirm");
  };

  const explain = (err: AxiosError<any>) => {
    const element = err.response?.data?.element;
    const code = REFUSALS.includes(element?.reason) ? element.reason : null;
    if (!code) return err.response?.data?.message || k("refusals.REFUSED_ANY");
    const text =
      code === "REFUSED"
        ? k("refusals.REFUSED", { message: err.response?.data?.message })
        : k(`refusals.${code}`);
    const left =
      code === "AMOUNT_TOO_HIGH" && typeof element.refundable === "number"
        ? ` ${k("refusals.refundableLeft", {
            amount: money(element.refundable, currency),
          })}`
        : "";
    return `${text}${left}`;
  };

  const submit = async () => {
    if (!entityId || !plan || cents === null) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      const response = await PaymentModuleService.refundUnderGuarantee(
        customerId,
        rootEntityId,
        entityId,
        {
          plan_code: plan.planCode,
          ...(cents !== undefined && { amount: cents }),
          reason: trimmedReason,
          request_id: requestId,
        }
      );
      setResult(response.data.element);
      setUncertain(false);
      setStep("done");
      onRefunded();
    } catch (error) {
      const err = error as AxiosError<any>;
      const status = err.response?.status;
      if (status && status >= 400 && status < 500) {
        setRefusal(`${explain(err)} ${k("nothingRefunded")}`);
        setUncertain(false);
        setRequestId(newRequestId());
        setStep("form");
      } else {
        setUncertain(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = (status: string) =>
    STRIPE_STATUSES.includes(status)
      ? k(`status.${status}`)
      : k("status.other");

  const footer =
    step === "form" ? (
      <div className="flex flex-wrap justify-end gap-2">
        <CustomButton
          onClick={onClose}
          variant="secondary"
          title={k("cancel")}
        />
        <CustomButton
          onClick={review}
          variant="primary"
          title={k("review")}
          disabled={!plan}
        />
      </div>
    ) : step === "confirm" ? (
      <div className="flex flex-wrap justify-end gap-2">
        {!uncertain && (
          <CustomButton
            onClick={() => setStep("form")}
            variant="secondary"
            title={k("back")}
            disabled={submitting}
          />
        )}
        <CustomButton
          onClick={submit}
          variant="danger"
          title={uncertain ? k("retry") : k("confirm")}
          isLoading={submitting}
          disabled={submitting}
        />
      </div>
    ) : (
      <div className="flex justify-end">
        <CustomButton
          onClick={onClose}
          variant="secondary"
          title={k("close")}
        />
      </div>
    );

  return (
    <ModalShell
      show={show}
      onClose={submitting || uncertain ? () => undefined : onClose}
      size="xl"
      dismissible={!submitting && !uncertain}
      showCloseButton={!uncertain}
      title={k("title")}
      subtitle={entityName}
      footer={footer}
    >
      {step === "form" && plan && (
        <div className="space-y-4">
          {refusal && (
            <AlertMessage color="failure">
              <span className="text-sm">{refusal}</span>
            </AlertMessage>
          )}

          <div>
            <FieldLabel htmlFor="guarantee-refund-plan" value={k("plan")} />
            <p
              id="guarantee-refund-plan"
              className="rounded-md border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-900"
            >
              {plan.planDescription
                ? `${plan.planDescription} (${plan.planCode})`
                : plan.planCode}
            </p>
          </div>

          <div>
            <FieldLabel
              htmlFor="guarantee-refund-amount"
              value={currency ? `${k("amount")} (${currency})` : k("amount")}
            />
            <input
              id="guarantee-refund-amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              className={`${INPUT} tabular-nums ${
                showErrors && cents === null ? "border-red-500" : ""
              }`}
            />
            <p className="mt-1 text-xs text-gray-500">
              {plan.lastPayment
                ? parsePayModDate(plan.lastPayment.date).isValid()
                  ? k("amountHint", {
                      amount: money(
                        plan.lastPayment.cents,
                        plan.lastPayment.currency
                      ),
                      date: formatPayModDate(plan.lastPayment.date, "L"),
                    })
                  : k("amountHintNoDate", {
                      amount: money(
                        plan.lastPayment.cents,
                        plan.lastPayment.currency
                      ),
                    })
                : k("amountHintUnknown")}
            </p>
            {showErrors && cents === null && (
              <p className="mt-1 text-xs text-red-600">{k("amountInvalid")}</p>
            )}
          </div>

          <div>
            <FieldLabel
              htmlFor="guarantee-refund-reason"
              value={k("reason")}
              required
            />
            <textarea
              id="guarantee-refund-reason"
              rows={3}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={k("reasonPlaceholder")}
              className={`${INPUT} ${
                showErrors && !trimmedReason ? "border-red-500" : ""
              }`}
            />
            <div className="mt-1 flex justify-between gap-3 text-xs">
              <span className="text-red-600">
                {showErrors && !trimmedReason ? k("reasonRequired") : ""}
              </span>
              <span className="tabular-nums text-gray-500">
                {reason.length} / {REASON_MAX}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-500">{k("subscriptionKept")}</p>
        </div>
      )}

      {step === "confirm" && plan && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{k("confirmLead")}</p>
          <dl className="grid grid-cols-[7.5rem_1fr] gap-x-3 gap-y-2 text-sm">
            <dt className="text-gray-500">{k("plan")}</dt>
            <dd className="text-gray-900">
              {plan.planDescription || plan.planCode}
            </dd>
            <dt className="text-gray-500">{k("amount")}</dt>
            <dd className="font-semibold tabular-nums text-gray-900">
              {cents !== undefined && cents !== null
                ? money(cents, currency)
                : k("wholeLastPayment")}
            </dd>
            <dt className="text-gray-500">{k("reason")}</dt>
            <dd className="whitespace-pre-wrap break-words text-gray-900">
              {trimmedReason}
            </dd>
          </dl>
          <p className="text-xs text-gray-500">{k("subscriptionKept")}</p>
          {uncertain && (
            <AlertMessage color="warning">
              <span className="text-sm">{k("uncertain")}</span>
            </AlertMessage>
          )}
        </div>
      )}

      {step === "done" && result && (
        <div className="space-y-4">
          <AlertMessage color="success">
            <span className="text-sm">{k("done")}</span>
          </AlertMessage>
          <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-2 text-sm">
            <dt className="text-gray-500">{k("result.amount")}</dt>
            <dd className="font-semibold tabular-nums text-gray-900">
              {money(result.amount_refunded, result.currency.toUpperCase())}
            </dd>
            <dt className="text-gray-500">{k("result.currency")}</dt>
            <dd className="text-gray-900">{result.currency.toUpperCase()}</dd>
            <dt className="text-gray-500">{k("result.refundId")}</dt>
            <dd className="min-w-0 font-mono text-xs text-gray-900">
              <Copy target={result.refund_id} truncate longVersion />
            </dd>
            <dt className="text-gray-500">{k("result.status")}</dt>
            <dd className="text-gray-900" title={result.status}>
              {statusLabel(result.status)}
            </dd>
          </dl>
        </div>
      )}
    </ModalShell>
  );
};

export default GuaranteeRefundModal;
